import React, { useState, useMemo, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import './App.css';
import { calculate, Expense, Settlement } from './calculation';
import AdBanner from './AdBanner'; // Import the new component

type SavedGroups = Record<string, string[]>;

function App() {
  const [participants, setParticipants] = useState<string[]>([]);
  const [newParticipant, setNewParticipant] = useState('');
  const [expenses, setExpenses] = useState<Expense[]>([]);
  
  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    paidBy: '',
    isTreat: false,
    participants: [] as string[],
  });

  const [savedGroups, setSavedGroups] = useState<SavedGroups>({});
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [isRounding, setRounding] = useState(false);

  const resultsRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    try {
      const storedGroups = localStorage.getItem('bbumbbai_groups');
      if (storedGroups) {
        setSavedGroups(JSON.parse(storedGroups));
      }
    } catch (error) {
      console.error("Failed to load groups from localStorage", error);
    }
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    try {
      localStorage.setItem('bbumbbai_groups', JSON.stringify(savedGroups));
    } catch (error) {
      console.error("Failed to save groups to localStorage", error);
    }
  }, [savedGroups]);

  useEffect(() => {
    setNewExpense(prev => ({ ...prev, participants }));
  }, [participants]);

  const handleAddParticipant = () => {
    if (newParticipant) { // Only proceed if there's a name
      let nameToAdd = newParticipant;
      let counter = 0;
      let originalName = newParticipant;

      // Check for duplicates and append #number
      while (participants.includes(nameToAdd)) {
        counter++;
        nameToAdd = `${originalName} #${counter}`;
      }

      const newParticipants = [...participants, nameToAdd];
      setParticipants(newParticipants);
      setNewExpense(prev => ({ ...prev, participants: [...prev.participants, nameToAdd] })); // Add to checklist
      setNewParticipant('');
      if (newParticipants.length === 1) { // If this is the first participant
        setNewExpense(prev => ({ ...prev, paidBy: nameToAdd })); // Set as default payer
      } else if (newParticipants.length > 1 && !newExpense.paidBy) { // If not the first participant and no payer selected
        setNewExpense(prev => ({ ...prev, paidBy: nameToAdd })); // Set new participant as default payer
      }
      setSelectedGroup('');
    }
  };

  const handleRemoveParticipant = (name: string) => {
    const newParticipants = participants.filter(p => p !== name);
    setParticipants(newParticipants);
    setExpenses(prevExpenses => prevExpenses.map(exp => ({ ...exp, participants: exp.participants.filter(p => p !== name) })).filter(exp => exp.paidBy !== name));
    setSelectedGroup('');
  };

  const handleAddExpense = () => {
    const amount = parseFloat(newExpense.amount);
    if (newExpense.description && !isNaN(amount) && amount > 0 && newExpense.paidBy && newExpense.participants.length > 0) {
      setExpenses([...expenses, { ...newExpense, amount }]);
      setNewExpense({ description: '', amount: '', paidBy: newExpense.paidBy, isTreat: false, participants: newExpense.participants });
    }
  };

  const handleRemoveExpense = (indexToRemove: number) => {
    setExpenses(expenses.filter((_, index) => index !== indexToRemove));
  };

  const handleExpenseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const isCheckbox = type === 'checkbox';
    setNewExpense(prev => ({ ...prev, [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value }));
  };

  const handleExpenseParticipantChange = (name: string) => {
    const currentParticipants = newExpense.participants;
    if (currentParticipants.includes(name)) {
      setNewExpense({ ...newExpense, participants: currentParticipants.filter(p => p !== name) });
    } else {
      setNewExpense({ ...newExpense, participants: [...currentParticipants, name] });
    }
  };

  const handleSaveGroup = () => {
    if (newGroupName && participants.length > 0) {
      setSavedGroups({ ...savedGroups, [newGroupName]: participants });
      setNewGroupName('');
      alert(`'${newGroupName}' 모임이 저장되었습니다!`);
    }
  };

  const handleLoadGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const groupName = e.target.value;
    setSelectedGroup(groupName);
    if (groupName && savedGroups[groupName]) {
      const loadedParticipants = savedGroups[groupName];
      setParticipants(loadedParticipants);
      setExpenses([]);
    } else if (groupName === '') {
      setParticipants([]);
      setExpenses([]);
    }
  };

  const handleDeleteGroup = () => {
    if (selectedGroup) {
      if (window.confirm(`'${selectedGroup}' 모임을 정말 삭제하시겠습니까?`)) {
        const { [selectedGroup]: _, ...remainingGroups } = savedGroups;
        setSavedGroups(remainingGroups);
        setSelectedGroup('');
      }
    }
  };

  const handleResetAll = () => {
    if (window.confirm("정말로 모든 내용을 초기화하시겠습니까? 저장된 모임은 그대로 유지됩니다.")) {
        setParticipants([]);
        setExpenses([]);
        setNewParticipant('');
        setNewExpense({
            description: '',
            amount: '',
            paidBy: '',
            isTreat: false,
            participants: [],
        });
        setSelectedGroup('');
        setRounding(false);
    }
  };

  const calculationResults = useMemo(() => calculate(participants, expenses), [participants, expenses]);

  const { finalSettlements, truncatedAmount } = useMemo(() => {
    if (!isRounding) {
      return { finalSettlements: calculationResults.settlements, truncatedAmount: 0 };
    }
    let totalTruncated = 0;
    const roundedSettlements = calculationResults.settlements.map(s => {
      const originalAmount = s.amount;
      const roundedAmount = Math.floor(originalAmount / 100) * 100;
      totalTruncated += originalAmount - roundedAmount;
      return { ...s, amount: roundedAmount };
    });
    return { finalSettlements: roundedSettlements, truncatedAmount: totalTruncated };
  }, [calculationResults.settlements, isRounding]);

  const handleSaveImage = () => {
    if (resultsRef.current) {
      html2canvas(resultsRef.current, { background: '#effffb' }).then(canvas => {
        const link = document.createElement('a');
        link.download = 'bbumbbai-result.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    }
  };

  const handleCopyText = () => {
    const { totalOwedByPerson, totalSpent, treats, owedBreakdown } = calculationResults;

    const expenseBreakdownText = expenses.filter(e => !e.isTreat).length > 0
      ? `[지출 항목별 총액]\n${expenses.filter(e => !e.isTreat).map(e => `• ${e.description}: ${e.amount.toLocaleString()}원`).join('\n')}\n\n`
      : '';

    const totalOwedText = Object.entries(totalOwedByPerson).map(([person, amount]) => {
      const breakdown = owedBreakdown[person] && owedBreakdown[person].length > 0
        ? ` (${owedBreakdown[person].map(item => item.description).join(', ')})`
        : '';
      return `• ${person}: ${amount.toLocaleString(undefined, {maximumFractionDigits: 0})}원${breakdown}`;
    }).join('\n');

    const settlementText = finalSettlements.map(s => `• ${s.from} → ${s.to}: ${s.amount.toLocaleString(undefined, {maximumFractionDigits: 0})}원`).join('\n') || '정산할 내역이 없어요.';
    const truncatedText = isRounding && truncatedAmount > 0 ? `\n\n총 ${truncatedAmount.toLocaleString(undefined, {maximumFractionDigits: 0})}원이 증발했습니다! 💨` : '';

    const summaryText = `[뿜빠이 정산 결과 💸]\n- 총 지출: ${totalSpent.toLocaleString()}원\n\n${expenseBreakdownText}[개인별 총 분담금]\n${totalOwedText}\n\n[정산해주세요!]\n${settlementText}${truncatedText}\n\n더치페이 정산은 뿜빠이! ➡️ https://bbumbbai.fantazm.net/`;

    const treatsText = treats.length > 0 ? `\n\n[오늘의 한턱! 🍻]\n${treats.map(t => `• ${t.paidBy}님이 ${t.description}(을)를 ${t.amount.toLocaleString()}원에 한턱 쐈습니다! 🎉`).join('\n')}` : '';

    navigator.clipboard.writeText(summaryText + treatsText).then(() => {
      alert('정산 결과가 복사되었습니다!');
    });
  };

  return (
    <div className="App">
      <header className="App-header"><h1>뿜빠이!</h1></header>

      {Object.keys(savedGroups).length > 0 && (
        <div className="group-section"><h2>모임 불러오기</h2><div className="input-group"><select onChange={handleLoadGroup} value={selectedGroup}><option value="">모임 선택...</option>{Object.keys(savedGroups).map(name => (<option key={name} value={name}>{name}</option>))}</select><button onClick={handleDeleteGroup} disabled={!selectedGroup} className="remove-btn">삭제</button></div></div>
      )}

      <div className="container">
        <div className="participants-section">
          <h2>참가자</h2>
          <div className="input-group"><input type="text" value={newParticipant} onChange={(e) => setNewParticipant(e.target.value)} placeholder="이름을 입력해요" onKeyPress={(e) => e.key === 'Enter' && handleAddParticipant()} /><button onClick={handleAddParticipant}>추가</button></div>
          <ul className="participant-list">{participants.map(p => (<li key={p} className="participant-item"><span>{p}</span><button onClick={() => handleRemoveParticipant(p)} className="remove-btn">삭제</button></li>))}
          </ul>
        </div>

        <div className="expenses-section">
          <h2>비용 추가</h2>
          <div className="form-group"><label>내용</label><input type="text" name="description" value={newExpense.description} onChange={handleExpenseChange} placeholder="예: 저녁, 간식" /></div>
          <div className="form-group"><label>금액</label><input type="number" name="amount" value={newExpense.amount} onChange={handleExpenseChange} placeholder="0" /></div>
          <div className="form-group"><label>결제한 사람</label><select name="paidBy" value={newExpense.paidBy} onChange={handleExpenseChange} disabled={participants.length === 0}><option value="">누가 냈나요?</option>{participants.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
          <div className="form-group"><label>이 비용에 참여한 사람은?</label><div className="expense-participants-list">{participants.map(p => (<div key={p} className="expense-participant-item"><input type="checkbox" id={`p-${p}`} checked={newExpense.participants.includes(p)} onChange={() => handleExpenseParticipantChange(p)} /><label htmlFor={`p-${p}`}>{p}</label></div>))}
          </div></div>
          <div className="checkbox-group"><input type="checkbox" name="isTreat" id="isTreat" checked={newExpense.isTreat} onChange={handleExpenseChange} /><label htmlFor="isTreat">한턱내기 (계산 제외)</label></div>
          <button onClick={handleAddExpense} className="add-expense-btn" disabled={!newExpense.description || !newExpense.paidBy || !(parseFloat(newExpense.amount) > 0) || newExpense.participants.length === 0}>비용 추가하기</button>
          <h2 style={{ marginTop: '30px' }}>비용 목록</h2>
          <ul className="expense-list">{expenses.map((expense, index) => (<li key={index} className="expense-item"><div className="expense-item-details"><div className="expense-item-description">{expense.description} {expense.isTreat && '(한턱!)'}</div><div className="expense-item-info">{expense.amount.toLocaleString()}원 / {expense.paidBy} / {expense.participants.length}명 참여</div></div><button onClick={() => handleRemoveExpense(index)} className="remove-btn">삭제</button></li>))}
          </ul>
        </div>
      </div>

      {participants.length >= 3 && selectedGroup === '' && (
        <div className="group-section save-section"><h2>이 모임 저장하기</h2><div className="input-group"><input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="저장할 모임 이름" /><button onClick={handleSaveGroup} disabled={!newGroupName}>저장</button></div></div>
      )}

      <AdBanner />

      <div className="results-section" ref={resultsRef}>
        <h2>정산 결과</h2>
        {participants.length > 1 ? (
          <>
            <div className="form-group rounding-group">
              <input type="checkbox" id="rounding" checked={isRounding} onChange={(e) => setRounding(e.target.checked)} />
              <label htmlFor="rounding">100원 단위로 정산하기</label>
            </div>
            <div className="summary-item"><span>총 지출:</span> {calculationResults.totalSpent.toLocaleString()}원</div>

            {expenses.filter(e => !e.isTreat).length > 0 && (
              <div className="expense-breakdown-section">
                <h4>지출 항목별 총액</h4>
                <ul>
                  {expenses.filter(e => !e.isTreat).map((e, i) => (
                    <li key={i}><span>{e.description}:</span> {e.amount.toLocaleString()}원</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="personal-owed-section">
              <h4>개인별 총 분담금</h4>
              <ul>
                {Object.entries(calculationResults.totalOwedByPerson).map(([person, amount]) => {
                  const participation = calculationResults.owedBreakdown[person];
                  const breakdownText = participation && participation.length > 0
                    ? `(${participation.map(item => item.description).join(', ')})`
                    : '';
                  return (
                    <li key={person} style={{ marginBottom: '0.5em', textAlign: 'center' }}>
                      <div>{person}: {amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}원</div>
                      {breakdownText && <div style={{ fontSize: '0.8em', color: '#666' }}>{breakdownText}</div>}
                    </li>
                  );
                })}
              </ul>
            </div>

            <h4>최종 정산</h4>
            <ul className="settlement-list">{finalSettlements.length > 0 ? finalSettlements.map((s, i) => (<li key={i} className="settlement-item"><span>{s.from}</span> <span className="arrow">→</span> <span>{s.to}</span>: {s.amount.toLocaleString(undefined, {maximumFractionDigits: 0})}원</li>)) : <div className="no-results">정산 완료! 다들 평화롭네요!</div>}
            </ul>
            {isRounding && truncatedAmount > 0 && (
                <div className="summary-item truncated-amount">
                    총 {truncatedAmount.toLocaleString(undefined, {maximumFractionDigits: 0})}원이 증발했습니다! 💨
                </div>
            )}
            {calculationResults.treats.length > 0 && (
              <div className="treats-section"><h3>오늘의 한턱! 🍻</h3><ul className="treat-list">{calculationResults.treats.map((t, i) => (<li key={i} className="treat-item"><span>{t.paidBy}</span>님이 <span>{t.description}</span>을(를) 위해 <span>{t.amount.toLocaleString()}</span>원에 한턱 쐈습니다! 🎉</li>))}
              </ul></div>
            )}
            
            <p style={{ marginTop: '20px', paddingTop: '10px', borderTop: '1px solid #eee', fontSize: '0.9em', color: '#777' }}>
              더치페이 정산은 뿜빠이! ➡️ https://bbumbbai.fantazm.net/
            </p>
          </>
        ) : (
          <div className="no-results">참가자를 2명 이상 추가하고 비용을 입력하면 정산 결과를 볼 수 있어요.</div>
        )}
      </div>
      
      <div className="sharing-buttons">
        {participants.length > 0 && <button onClick={handleResetAll} className="remove-btn">리셋</button>}
        {participants.length > 1 && <button onClick={handleCopyText} className="copy-btn">텍스트로 복사</button>}
        {participants.length > 1 && <button onClick={handleSaveImage} className="image-btn">이미지로 저장</button>}
      </div>

      <footer className="app-footer">
        <p>
          <span>Copyright © 2025 fantazm</span>
          <span className="footer-separator">|</span>
          <span>Bug reports: <a href="mailto:admin@fantazm.net">admin@fantazm.net</a></span>
        </p>
      </footer>
    </div>
  );
}

export default App;